<template>
  <router-view/>

  <!-- ── Modal de boas-vindas (pós-login) ── -->
  <transition name="client-fade">
    <div v-if="showClientModal" class="cm-overlay">
      <div class="cm-card">

        <!-- Left: photo -->
        <div class="cm-photo-col">
          <img src="@/assets/welcome-modal-bg.jpg" alt="" class="cm-photo" />
        </div>

        <!-- Right: form -->
        <div class="cm-form-col">
          <h2 class="cm-title" v-if="selectedClient">Trocar<br>de cliente</h2>
          <h2 class="cm-title" v-else>Bem-vindo<br>ao Prompta</h2>
          <p class="cm-subtitle" v-if="selectedClient">Cliente atual: <strong>{{ selectedClient.name }}</strong></p>
          <p class="cm-subtitle" v-else>Selecione o cliente para começar.</p>

          <!-- Loading -->
          <div v-if="loadingClients" class="cm-state">
            <svg class="cm-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="#00b9f9" stroke-width="3"
                      stroke-linecap="round" stroke-dasharray="31.4 31.4"/>
            </svg>
            Carregando…
          </div>

          <!-- Error -->
          <div v-else-if="clientError" class="cm-error">{{ clientError }}</div>

          <!-- Fields -->
          <template v-else>
            <div class="cm-field">
              <span class="cm-field-label">CLIENTE</span>
              <div class="cm-select-wrap">
                <select v-model="chosenClientId" class="cm-select">
                  <option value="">Selecione…</option>
                  <option v-for="c in clients" :key="c.id" :value="c.id">{{ c.name }}</option>
                </select>
                <svg class="cm-chevron" width="11" height="7" viewBox="0 0 12 7" fill="none">
                  <path d="M1 1l5 5 5-5" stroke="#555" stroke-width="1.8"
                        stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </div>
            </div>

            <button class="cm-btn" :disabled="!chosenClientId" @click="confirmClient">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" class="cm-btn-icon">
                <circle cx="7" cy="7" r="6.5" stroke="#fff" stroke-width="1"/>
                <path d="M5.5 4.5l5 2.5-5 2.5V4.5z" fill="#fff"/>
              </svg>
              {{ selectedClient ? 'Confirmar' : 'Iniciar' }}
            </button>
          </template>
        </div>

      </div>
    </div>
  </transition>
</template>

<script setup>
import { ref, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import { useSelectedClient } from '@/composables/useSelectedClient'
import { useClientModal } from '@/composables/useClientModal'
import config from '@/config'

const backendUrl = config.backendUrl
const router = useRouter()
const route  = useRoute()
const { user, loadUser } = useAuth()
const { setClient, hasClient, selectedClient } = useSelectedClient()
const { _openCount } = useClientModal()

const showClientModal = ref(false)
const clients         = ref([])
const chosenClientId  = ref('')
const loadingClients  = ref(false)
const clientError     = ref('')
const _modalTriggered = ref(false)

const PUBLIC_ROUTES = ['login']

async function maybeShowModal() {
  if (PUBLIC_ROUTES.includes(route.name)) return
  if (_modalTriggered.value) return   // already shown in this JS process lifetime
  if (hasClient()) return             // client already selected (persisted in localStorage)

  if (!user.value?.id) await loadUser()
  if (!user.value?.id) return         // not authenticated

  _modalTriggered.value = true
  await openModal()
}

async function openModal() {
  if (PUBLIC_ROUTES.includes(route.name)) return
  if (!user.value?.id) await loadUser()
  if (!user.value?.id) return

  await fetchClients()

  // Pre-select current client if switching, otherwise pick first
  const currentId = selectedClient.value?.id
  if (currentId && clients.value.some(c => c.id === currentId)) {
    chosenClientId.value = currentId
  } else if (clients.value.length > 0) {
    chosenClientId.value = clients.value[0].id
  }

  showClientModal.value = true
}

async function fetchClients() {
  loadingClients.value = true
  clientError.value = ''
  try {
    const res = await fetch(`${backendUrl}/api/companies/`, { credentials: 'include' })
    if (!res.ok) throw new Error(`Erro ${res.status}`)
    clients.value = await res.json()
    if (clients.value.length === 0) {
      clientError.value = 'Nenhum cliente disponível. Contate o administrador.'
    }
  } catch (e) {
    clientError.value = 'Não foi possível carregar os clientes.'
    console.error('[App] fetchClients:', e)
  } finally {
    loadingClients.value = false
  }
}

function confirmClient() {
  const client = clients.value.find(c => c.id === chosenClientId.value)
  if (!client) return
  setClient(client)
  showClientModal.value = false
}

onMounted(async () => {
  await router.isReady()
  await maybeShowModal()
})

// Trigger when user becomes available (OAuth redirect resolves asynchronously)
watch(() => user.value?.id, async (id) => {
  if (id) await maybeShowModal()
})

// Trigger from UserMenuButton "Trocar de cliente"
watch(_openCount, async (n) => {
  if (n > 0) await openModal()
})
</script>

<style>
@import url("https://fonts.googleapis.com/css2?family=Figtree:wght@300;400;500;600;700&family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");

#app {
  font-family: 'Figtree', 'Inter', Arial, Helvetica, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  margin: 0; padding: 0; box-sizing: border-box;
}

body {
  font-family: 'Figtree', 'Inter', Arial, Helvetica, sans-serif;
  margin: 0; padding: 0; box-sizing: border-box;
}

/* ── Welcome modal ──────────────────────────────────────────── */
.client-fade-enter-active { transition: opacity 0.25s ease; }
.client-fade-leave-active { transition: opacity 0.18s ease; }
.client-fade-enter-from,
.client-fade-leave-to    { opacity: 0; }

/* Dimmed overlay */
.cm-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  padding: 20px;
}

/* Horizontal card */
.cm-card {
  display: flex;
  width: 100%;
  max-width: 720px;
  min-height: 380px;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.28);
  animation: cm-in 0.28s cubic-bezier(0.34, 1.3, 0.64, 1);
}

@keyframes cm-in {
  from { opacity: 0; transform: scale(0.94) translateY(10px); }
  to   { opacity: 1; transform: scale(1) translateY(0); }
}

/* Left: photo */
.cm-photo-col {
  flex: 0 0 45%;
  position: relative;
  overflow: hidden;
}

.cm-photo {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
}

/* Right: form */
.cm-form-col {
  flex: 1;
  background: #eaf4f8;
  padding: 44px 40px 40px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 0;
  box-sizing: border-box;
}

.cm-title {
  font-family: 'Figtree', sans-serif;
  font-size: 30px;
  font-weight: 700;
  color: #111828;
  margin: 0 0 10px;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.cm-subtitle {
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #555;
  margin: 0 0 28px;
  line-height: 1.5;
}

/* Loading */
.cm-state {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Figtree', sans-serif;
  font-size: 14px;
  color: #666;
}

.cm-spinner { animation: spin 1s linear infinite; flex-shrink: 0; }
@keyframes spin { to { transform: rotate(360deg); } }

/* Error */
.cm-error {
  background: #fee2e2;
  color: #dc2626;
  padding: 10px 14px;
  border-radius: 10px;
  font-family: 'Figtree', sans-serif;
  font-size: 13px;
  border: 1px solid #fca5a5;
}

/* Field */
.cm-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 16px;
}

.cm-field-label {
  font-family: 'Figtree', sans-serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #888;
  text-transform: uppercase;
}

/* Select */
.cm-select-wrap {
  position: relative;
}

.cm-select {
  width: 100%;
  height: 48px;
  background: #fff;
  border: 1.5px solid #d5e5ec;
  border-radius: 12px;
  padding: 0 42px 0 16px;
  font-family: 'Figtree', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
  appearance: none;
  -webkit-appearance: none;
  cursor: pointer;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
}

.cm-select:focus { border-color: #00b9f9; }
.cm-select option[value=""] { color: #999; }

.cm-chevron {
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

/* Button */
.cm-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 100%;
  height: 48px;
  background: #00b9f9;
  border: none;
  border-radius: 30px;
  font-family: 'Figtree', sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  margin-top: 4px;
  transition: background 0.15s, opacity 0.15s;
}

.cm-btn:hover:not(:disabled) { background: #00a8e3; }
.cm-btn:disabled { opacity: 0.45; cursor: not-allowed; }
.cm-btn-icon { flex-shrink: 0; }

/* Responsive */
@media (max-width: 600px) {
  .cm-card { flex-direction: column; max-width: 360px; min-height: unset; }
  .cm-photo-col { flex: 0 0 180px; }
  .cm-form-col { padding: 28px 24px 32px; }
  .cm-title { font-size: 24px; }
}
</style>
